<template>
    <VAppLayout>
        <VPageHeader :title="comCategory.name || $t('pages.menu.title')" back @back="goBack" />
        <VContainer>
            <div class="menu-single">
                <p v-if="comCategory.description" class="menu-category-description">
                    {{ comCategory.description }}
                </p>

                <ul class="menu-single__list">
                    <MenuItem v-for="item in comFilteredItems"
                              :item="item"
                              :key="item.id" />
                </ul>

                <div v-if="comCategory.subcategories">
                    <div v-for="subcategory in comCategory.subcategories"
                         class="menu-subcategory"
                         :key="subcategory.category_id">
                        <p class="menu-subcategory-name">{{ subcategory.name }}</p>
                        <ul class="menu-single__list">
                            <MenuItem v-for="item in subcategory.items"
                                      :item="item"
                                      :key="item.id" />
                        </ul>
                    </div>
                </div>
            </div>
        </VContainer>
    </VAppLayout>
</template>

<script>
import VAppLayout from "@/layouts/VAppLayout.vue";
import VContainer from "@/components/ui/VContainer.vue";
import VPageHeader from "@/components/ui/VPageHeader.vue";
import MenuItem from "./MenuItem.vue";
import applyHead from "@/utils/head";

export default {
    name: "MenuSingle",
    components: {
        VAppLayout,
        VContainer,
        VPageHeader,
        MenuItem,
    },
    props: {
        id: null,
    },
    created() {
        this.$store.dispatch('menuStore/GET_FULL_MENU');
    },
    watch: {
        // Menu loads async after this page mounts — once the category resolves,
        // refresh the tab title to "<Category> — <Venue>".
        comCategory() {
            applyHead(this.$route);
        },
    },
    computed: {
        comCategory() {
            const popular = this.$store.getters['menuStore/getPopular'];
            if (popular && popular.id === this.id) {
                return popular;
            }
            const menu = this.$store.getters['menuStore/getMenu'] || [];
            const category = menu.find(c => c.id == this.id);

            return category || {};
        },
        comFilteredItems() {
            let res = this.comCategory.items || [];

            if (this.comCategory.subcategories && this.comCategory.subcategories.length !== 0) {
                const subItems = this.comCategory.subcategories.reduce((arr, val) => {
                    return arr.concat(val.items);
                }, []);

                res = res.filter(item => {
                    return !subItems.some(subItem => subItem.category_id === item.category_id);
                });
            }

            return res;
        }
    },
    methods: {
        goBack() {
            this.$router.back();
        }
    }
};
</script>

<style lang="scss" scoped>
.menu-single {
    padding-top: 8px;
    /* clear the floating bottom nav (content overflows VContainer, so the layout
       reserve is bypassed — carry the clearance here) */
    padding-bottom: calc(var(--nav-height) + var(--nav-float-gap) * 2 + var(--safe-bottom) + 24px);
}

.menu-single__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    /* MenuItem's own vertical margins don't collapse inside a flex column, so
       they'd stack on top of the gap. Zero them here and let gap be the single
       source of spacing between cards. */
    ::v-deep .menu-item-li {
        margin: 0;
    }
}

.menu-category-description {
    margin: 0 0 14px;
    padding: 14px 16px;

    font-weight: var(--weight-regular);
    font-size: 14px;
    line-height: 1.4;
    text-align: center;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-xl);

    color: var(--text-secondary);
    background: var(--surface-card);
}

.menu-subcategory-name {
    margin: 22px 0 10px;

    font-weight: var(--weight-bold);
    font-size: 12px;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    color: var(--text-muted);
}
</style>
